import { ArrowsAltOutlined, ShrinkOutlined } from '@ant-design/icons'
import { useEffect, useRef, useState } from 'react'

import cn from './ExpandableInput.module.scss'

type ExpandableInputPropsType = {
  value?: string
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void
  readOnly?: boolean
  placeholder?: string
  name?: string
}

function ExpandableInput({
  value,
  onChange,
  readOnly,
  placeholder,
  name,
}: ExpandableInputPropsType) {
  const [isExpanded, setIsExpanded] = useState(false)
  const [innerText, setInnerText] = useState(value || '')
  const containerRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    setInnerText(value || '')
  }, [value])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsExpanded(false)
      }
    }
    if (isExpanded) document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isExpanded])

  useEffect(() => {
    if (isExpanded && textareaRef.current && !readOnly) {
      textareaRef.current.focus()
      const len = textareaRef.current.value.length
      textareaRef.current.setSelectionRange(len, len)
      adjustHeight(textareaRef.current)
    }
  }, [isExpanded, readOnly])

  const adjustHeight = (textarea: HTMLTextAreaElement) => {
    textarea.style.height = 'auto'
    const maxHeight = 300
    const newHeight = Math.min(textarea.scrollHeight, maxHeight)
    textarea.style.height = `${newHeight}px`
    textarea.style.overflowY =
      textarea.scrollHeight > maxHeight ? 'auto' : 'hidden'
  }

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInnerText(e.target.value)
    if (onChange) {
      onChange(e)
    }
  }

  return (
    <div className={cn.expandableInput} ref={containerRef}>
      <button
        type="button"
        className={cn.toggleButton}
        onClick={(e) => {
          e.stopPropagation()
          setIsExpanded(!isExpanded)
        }}
        title={isExpanded ? 'Свернуть' : 'Развернуть для редактирования'}
      >
        {isExpanded ? <ShrinkOutlined /> : <ArrowsAltOutlined />}
      </button>

      {isExpanded && (
        <div className={cn.expandedContent}>
          <textarea
            ref={textareaRef}
            value={innerText}
            name={name}
            onChange={handleChange}
            readOnly={readOnly}
            onInput={(e) => adjustHeight(e.currentTarget)}
            placeholder={readOnly ? 'Поле только для чтения' : placeholder}
            className={[cn.textarea, readOnly ? cn.readOnly : ''].join(' ')}
            rows={3}
          />
        </div>
      )}
    </div>
  )
}

export default ExpandableInput
